import { RefreshCw } from 'lucide-react';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/components/ui/Button/Button';
import { Input } from '@/shared/components/ui/Input/Input';
import { slugify } from '@/shared/utils/slugify/slugify.util';

import { type SlugFieldProps } from './SlugField.types';

/**
 * A machine key, derived from a name but always editable.
 *
 * Auto-derivation is driven from the SOURCE field's change handler rather than
 * from an effect here (`DynamicForm` owns that wiring): an effect that wrote the
 * form on every render of a sibling is both a `set-state-in-effect` violation
 * and a race that fights the operator's own typing.
 *
 * The interesting case in this product is Arabic. `slugify` is ASCII-only by
 * design, so a role named "موظف تحقق" derives to nothing — and rather than
 * leaving a mysteriously empty required field, the hint says so and hands the
 * job back. A portal that is Arabic-first has to answer this out loud.
 *
 * **Ref and blur both reach the input** (P10.2d). Without the ref
 * `shouldFocusError` had nothing to focus, so an invalid key was reported and
 * never reached; without `onBlur` the field was never *touched*, so
 * `mode: 'onTouched'` never validated it until the first submit.
 */
export const SlugField = forwardRef<HTMLInputElement, SlugFieldProps>(function SlugField(
  {
    id,
    value,
    onChange,
    onManualEdit,
    onBlur,
    sourceValue,
    hasSource = true,
    separator = '-',
    maxLength,
    isLocked = false,
    isInvalid = false,
    placeholder,
    describedById,
  },
  ref,
) {
  const { t } = useTranslation('uiKit');

  const derived = slugify(sourceValue, { separator, maxLength });
  const isUntranslatable = sourceValue.trim() !== '' && derived === '';

  return (
    <div className="flex flex-col gap-xs">
      <div className="flex items-center gap-sm">
        <Input
          ref={ref}
          id={id}
          value={value}
          // A key is Latin whatever the UI language is, so the field is LTR even
          // in an Arabic portal — otherwise the caret and any punctuation land
          // on the wrong side of what the operator is typing.
          dir="ltr"
          disabled={isLocked}
          isInvalid={isInvalid}
          placeholder={placeholder}
          aria-describedby={describedById}
          onBlur={onBlur}
          onChange={(event) => {
            onManualEdit();
            onChange(event.target.value);
          }}
        />
        {isLocked || !hasSource ? null : (
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label={t('form.slugFromName')}
            disabled={derived === ''}
            onClick={() => {
              onManualEdit();
              onChange(derived);
            }}
          >
            <RefreshCw aria-hidden className="h-4 w-4" />
          </Button>
        )}
      </div>

      {isLocked ? (
        <p className="text-caption text-muted-foreground">{t('form.slugLocked')}</p>
      ) : null}
      {isUntranslatable ? (
        <p className="text-caption text-muted-foreground">{t('form.slugUntranslatable')}</p>
      ) : null}
    </div>
  );
});
